import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Wine, Leaf, Cigarette, Gift, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import UnstampCodeModal from './UnstampCodeModal';
import { toast } from 'sonner';

const cardConfig = {
  alcohol: {
    title: 'Carte Alcool',
    icon: Wine,
    reward: 'Bouteille gratuite',
    description: '1 tampon par bouteille achetée',
    color: '#722F37',
    secondaryColor: '#F3E5E7',
    accentColor: '#D4AF37',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/ssv1zkgy_1.png'
  },
  cbd: {
    title: 'Carte CBD',
    icon: Leaf,
    reward: 'Sachet 2g gratuit',
    description: '1 tampon par achat de 2g',
    color: '#2D5A45',
    secondaryColor: '#E8F5E9',
    accentColor: '#66BB6A',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/5qfu6xxl_5.png'
  },
  puffs: {
    title: 'Carte Puffs',
    icon: Cigarette,
    reward: 'Puff gratuit par ligne',
    description: '1 tampon par puff acheté',
    color: '#6366F1',
    secondaryColor: '#EEF2FF',
    accentColor: '#06B6D4',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/f35iwlob_3.png',
    lines: ['20k', '28k', '32k', '36k']
  }
};

export default function LoyaltyCard({ card, onStamp, onRedeem }) {
  const [showUnstampModal, setShowUnstampModal] = useState(false);
  const [pendingUnstampIndex, setPendingUnstampIndex] = useState(null);
  const [clickCount, setClickCount] = useState({});
  const [clickTimeout, setClickTimeout] = useState(null);
  
  const config = cardConfig[card.type];
  const Icon = config.icon;
  
  const stampedCount = card.boxes.filter(b => b).length;
  const totalBoxes = card.type === 'puffs' ? 10 : 10; // Per line for puffs
  const remainingBoxes = card.type === 'puffs' ? 0 : totalBoxes - stampedCount;
  const isNearCompletion = remainingBoxes <= 2 && remainingBoxes > 0;
  
  // Check if any line is complete for puffs
  const completedLines = card.type === 'puffs' 
    ? config.lines.map((_, idx) => {
        const lineStart = idx * 10;
        const lineEnd = lineStart + 10;
        return card.boxes.slice(lineStart, lineEnd).every(b => b);
      })
    : [];
  
  const hasCompletedLine = completedLines.some(completed => completed);
  const isFullyComplete = card.type !== 'puffs' && stampedCount === totalBoxes;

  const handleBoxClick = (index) => {
    const isCurrentlyStamped = card.boxes[index];
    
    if (isCurrentlyStamped) {
      // Gérer le double-clic pour décocher
      const currentCount = clickCount[index] || 0;
      
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
      
      if (currentCount === 0) {
        // Premier clic
        setClickCount({ ...clickCount, [index]: 1 });
        toast.info('Cliquez à nouveau pour décocher');
        
        const timeout = setTimeout(() => {
          setClickCount({ ...clickCount, [index]: 0 });
        }, 2000);
        setClickTimeout(timeout);
      } else {
        // Deuxième clic - demander le code
        setClickCount({ ...clickCount, [index]: 0 });
        setPendingUnstampIndex(index);
        setShowUnstampModal(true);
      }
    } else {
      // Cocher directement
      onStamp(card.id, index, true);
    }
  };

  const handleUnstampWithCode = async (code) => {
    try {
      await onStamp(card.id, pendingUnstampIndex, false, code);
      toast.success('Case décochée');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || '❌ Code invalide. Vérifiez auprès du vendeur.');
      return false;
    }
  };

  const handleRedeemClick = (lineIndex = null) => {
    onRedeem(card.id, card.type, lineIndex);
  };

  const renderStampGrid = () => {
    if (card.type === 'puffs') {
      return (
        <div className="space-y-4">
          {config.lines.map((lineName, lineIdx) => {
            const lineStart = lineIdx * 10;
            const lineEnd = lineStart + 10;
            const lineBoxes = card.boxes.slice(lineStart, lineEnd);
            const lineCompleted = lineBoxes.every(b => b);
            const lineStamped = lineBoxes.filter(b => b).length;
            const lineRemaining = 10 - lineStamped;
            const lineNearCompletion = lineRemaining <= 2 && lineRemaining > 0;

            return (
              <div
                key={lineIdx}
                className={cn(
                  'p-4 rounded-xl border-2',
                  lineNearCompletion && 'animate-pulse-border',
                  lineCompleted && 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500'
                )}
                style={{
                  borderColor: lineCompleted ? '#10B981' : lineNearCompletion ? '#8B5CF6' : '#E2E8F0'
                }}
                data-testid={`puffs-line-${lineIdx}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-bold text-sm" style={{ color: config.color }}>
                    {lineName}
                  </span>
                  {lineCompleted && (
                    <Button
                      size="sm"
                      onClick={() => handleRedeemClick(lineIdx)}
                      className="h-8 px-3 text-xs font-semibold rounded-lg"
                      style={{ backgroundColor: config.accentColor }}
                      data-testid={`redeem-line-${lineIdx}-button`}
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      Échanger
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-10 gap-1">
                  {lineBoxes.map((isStamped, boxIdx) => (
                    <button
                      key={boxIdx}
                      onClick={() => handleBoxClick(lineStart + boxIdx)}
                      className={cn(
                        'aspect-square rounded-md border-2 flex items-center justify-center min-w-0',
                        'hover:scale-105 active:scale-95 transition-transform',
                        isStamped && 'stamp-animation'
                      )}
                      style={{
                        backgroundColor: isStamped ? config.color : 'white',
                        borderColor: isStamped ? config.color : '#CBD5E1',
                        minWidth: '24px',
                        maxWidth: '40px'
                      }}
                      data-testid={`stamp-box-${lineStart + boxIdx}`}
                    >
                      {isStamped && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-5 gap-3">
        {card.boxes.map((isStamped, index) => (
          <button
            key={index}
            onClick={() => handleBoxClick(index)}
            className={cn(
              'aspect-square rounded-xl border-2 flex items-center justify-center',
              'hover:scale-110 active:scale-95',
              isStamped && 'stamp-animation'
            )}
            style={{
              backgroundColor: isStamped ? config.color : 'white',
              borderColor: isStamped ? config.color : '#CBD5E1'
            }}
            data-testid={`stamp-box-${index}`}
          >
            {isStamped && <CheckCircle2 className="w-6 h-6 text-white" />}
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          'relative bg-white rounded-3xl shadow-xl overflow-hidden',
          (isNearCompletion || hasCompletedLine) && 'animate-pulse-border'
        )}
        style={{
          borderWidth: '3px',
          borderStyle: 'solid',
          borderColor: isNearCompletion || hasCompletedLine ? '#8B5CF6' : 'transparent'
        }}
        data-testid={`loyalty-card-${card.type}`}
      >
      {/* Header with Background Image */}
      <div
        className="relative h-36 flex items-center justify-between px-6"
        style={{
          background: `linear-gradient(135deg, ${config.color}99 0%, ${config.accentColor}99 100%)`,
          backgroundImage: `linear-gradient(135deg, ${config.color}90 0%, ${config.accentColor}90 100%), url(${config.bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top'
        }}
      >
        <div className="text-white">
          <div className="flex items-center gap-3 mb-2">
            <Icon className="w-8 h-8" />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {config.title}
            </h2>
          </div>
          <p className="text-sm opacity-90">{config.description}</p>
        </div>
        <div className="text-white text-right">
          <div className="text-3xl font-bold">
            {card.type === 'puffs' ? '-' : `${stampedCount}/10`}
          </div>
          <div className="text-xs opacity-90">tampons</div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-6">
        {renderStampGrid()}

        {/* Reward Info */}
        <div
          className="mt-6 p-4 rounded-xl flex items-center justify-between"
          style={{ backgroundColor: config.secondaryColor }}
        >
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6" style={{ color: config.color }} />
            <div>
              <div className="font-semibold" style={{ color: config.color }}>
                Cadeau gratuit
              </div>
              <div className="text-sm text-gray-600">{config.reward}</div>
            </div>
          </div>
          {isFullyComplete && card.type !== 'puffs' && (
            <Button
              onClick={() => handleRedeemClick()}
              className="font-semibold"
              style={{ backgroundColor: config.accentColor }}
              data-testid="redeem-card-button"
            >
              Échanger
            </Button>
          )}
        </div>

        {/* Near Completion Badge */}
        {(isNearCompletion || hasCompletedLine) && (
          <div
            className="mt-4 p-3 rounded-xl text-center font-semibold"
            style={{ backgroundColor: '#F3E8FF', color: '#8B5CF6' }}
            data-testid="near-completion-badge"
          >
            {card.type === 'puffs' ? 'Ligne complète !' : `Plus que ${remainingBoxes} tampon${remainingBoxes > 1 ? 's' : ''} !`}
          </div>
        )}

        {/* Avertissements légaux */}
        {card.type === 'alcohol' && (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500 italic px-4">
            <AlertTriangle className="w-3 h-3" />
            <span>L&apos;abus d&apos;alcool est dangereux pour la santé, à consommer avec modération</span>
          </div>
        )}
        {card.type === 'puffs' && (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-orange-400 italic px-4">
            <AlertTriangle className="w-3 h-3" />
            <span>La nicotine est fortement addictive. Usage non recommandé aux non-fumeurs.</span>
          </div>
        )}
        {card.type === 'cbd' && (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-amber-400 italic px-4">
            <AlertTriangle className="w-3 h-3" />
            <span>Le CBD peut altérer la vigilance. Ne conduisez pas après consommation.</span>
          </div>
        )}
      </div>
    </motion.div>

    {/* Unstamp Code Modal */}
    <UnstampCodeModal
      isOpen={showUnstampModal}
      onClose={() => {
        setShowUnstampModal(false);
        setPendingUnstampIndex(null);
      }}
      onSubmit={handleUnstampWithCode}
    />
  </>
  );
}